/**
 * Roof Master — Column Manager (Ships 2 + 3 of the editable-grid build).
 *
 * Manager-only "Customize columns" drawer. Reorder (native HTML5 drag),
 * show/hide, set width, rename header, and create/delete user-defined custom
 * fields. Saves the ONE shared layout (roof_master_layout) — Greg's call:
 * everyone sees the same column setup.
 *
 * The anchor column (homeowner_name / the DealLink) is pinned first and can't
 * be hidden or moved. Native drag = no new dependency.
 */
import { useEffect, useState } from "react";
import {
  orderedColumns,
  LAYOUT_PINNED_FIRST,
  type ColumnDef,
  type CustomFieldDef,
  type LayoutEntry,
  type OrderedColumn,
} from "./roofMasterColumns";

interface ColumnManagerProps {
  layout: LayoutEntry[] | null;
  customCols: ColumnDef[];
  saving: boolean;
  onSave: (entries: LayoutEntry[]) => void;
  onReset: () => void;
  onCreateField: (label: string, fieldType: CustomFieldDef["field_type"], options: { value: string; label: string }[]) => void;
  onDeleteField: (cfKey: string) => void;
  onClose: () => void;
}

const FIELD_TYPES: { value: CustomFieldDef["field_type"]; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & time" },
  { value: "boolean", label: "Checkbox" },
  { value: "select", label: "Dropdown" },
];

export default function RoofMasterColumnManager({ layout, customCols, saving, onSave, onReset, onCreateField, onDeleteField, onClose }: ColumnManagerProps) {
  const [rows, setRows] = useState<OrderedColumn[]>(() => orderedColumns(layout, customCols));
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  // New-field form
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<CustomFieldDef["field_type"]>("text");
  const [newOptions, setNewOptions] = useState("");

  // Reconcile rows when custom fields are created/deleted while the drawer is
  // open: keep existing rows (preserving in-progress order/edits), append new
  // columns, drop removed ones.
  useEffect(() => {
    setRows((prev) => {
      const universe = orderedColumns(layout, customCols);
      const uni = new Map(universe.map((o) => [o.col.key, o]));
      const kept = prev.filter((r) => uni.has(r.col.key));
      const keptKeys = new Set(kept.map((r) => r.col.key));
      const added = universe.filter((o) => !keptKeys.has(o.col.key));
      return [...kept, ...added];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customCols]);

  const submitNewField = () => {
    const label = newLabel.trim();
    if (!label) return;
    const opts = newType === "select"
      ? newOptions.split(",").map((s) => s.trim()).filter(Boolean).map((s) => ({ value: s, label: s }))
      : [];
    onCreateField(label, newType, opts);
    setNewLabel(""); setNewType("text"); setNewOptions(""); setShowAdd(false);
  };

  const setHidden = (i: number, hidden: boolean) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, hidden } : r)));
  const setCol = (i: number, patch: Partial<OrderedColumn["col"]>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, col: { ...r.col, ...patch } } : r)));

  const move = (from: number, to: number) => {
    let dest = to < 1 ? 1 : to; // never above the pinned anchor (index 0)
    if (from === dest) return;
    setRows((rs) => {
      const next = [...rs];
      const [m] = next.splice(from, 1);
      if (dest > from) dest -= 1; // account for the removed item
      next.splice(dest, 0, m);
      return next;
    });
  };

  const onDrop = (i: number) => {
    if (dragIdx != null && dragIdx !== i) move(dragIdx, i);
    setDragIdx(null);
  };

  const handleSave = () => {
    const entries: LayoutEntry[] = rows.map((r) => ({
      key: r.col.key,
      hidden: r.hidden || undefined,
      w: r.col.width,
      label: r.col.label,
    }));
    onSave(entries);
  };

  const shownCount = rows.filter((r) => !r.hidden).length;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md h-full shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-bardin-ink">Customize columns</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Drag to reorder · check to show · {shownCount} of {rows.length} shown.
            Changes apply to <span className="font-semibold">everyone</span> (shared layout).
          </p>
        </div>

        {/* Column list */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {rows.map((r, i) => {
            const pinned = r.col.key === LAYOUT_PINNED_FIRST;
            return (
              <div
                key={r.col.key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                className={
                  "flex items-center gap-2 px-2 py-1.5 rounded border mb-1 text-[12px] " +
                  (dragIdx === i ? "border-bardin-orange bg-orange-50 " : "border-slate-200 ") +
                  (r.hidden ? "opacity-60 " : "") +
                  (pinned ? "bg-slate-50" : "bg-white")
                }
              >
                {/* Only the handle is draggable, so the label/width inputs stay editable. */}
                <span
                  draggable={!pinned}
                  onDragStart={() => { if (!pinned) setDragIdx(i); }}
                  onDragEnd={() => setDragIdx(null)}
                  className={"text-slate-400 select-none " + (pinned ? "" : "cursor-grab")}
                  title={pinned ? "Pinned first" : "Drag to reorder"}
                >
                  {pinned ? "🔒" : "⠿"}
                </span>
                <input
                  type="checkbox"
                  checked={!r.hidden}
                  disabled={pinned}
                  onChange={(e) => setHidden(i, !e.target.checked)}
                  className="cursor-pointer"
                  title={pinned ? "Always shown" : "Show / hide"}
                />
                <input
                  type="text"
                  value={r.col.label}
                  onChange={(e) => setCol(i, { label: e.target.value })}
                  className="flex-1 min-w-0 border border-slate-200 rounded px-1.5 py-0.5 text-[12px]"
                />
                <span className="text-[10px] text-slate-400 w-20 shrink-0 truncate" title={r.col.group}>{r.col.group}</span>
                <input
                  type="number"
                  value={r.col.width ?? ""}
                  min={60}
                  step={10}
                  onChange={(e) => setCol(i, { width: e.target.value ? Number(e.target.value) : undefined })}
                  className="w-14 border border-slate-200 rounded px-1 py-0.5 text-[11px] text-right"
                  title="Column width (px)"
                />
                {r.col.gated && <span className="text-[9px] text-amber-600" title="Manager-only ($)">$</span>}
                {r.col.custom && (
                  <button
                    onClick={() => onDeleteField(r.col.cfKey as string)}
                    className="text-slate-300 hover:text-red-600 shrink-0"
                    title="Delete custom field (saved values are kept)"
                  >
                    🗑
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Add custom field */}
        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50">
          {showAdd ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitNewField(); }}
                  placeholder="New field name (e.g. Permit #)"
                  className="flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 text-[12px]"
                />
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as CustomFieldDef["field_type"])}
                  className="border border-slate-300 rounded px-1.5 py-1 text-[12px] bg-white"
                >
                  {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {newType === "select" && (
                <input
                  value={newOptions}
                  onChange={(e) => setNewOptions(e.target.value)}
                  placeholder="Dropdown choices, comma-separated"
                  className="w-full border border-slate-300 rounded px-2 py-1 text-[12px]"
                />
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowAdd(false); setNewLabel(""); setNewOptions(""); }} className="px-2.5 py-1 text-xs text-slate-600">Cancel</button>
                <button onClick={submitNewField} disabled={!newLabel.trim()} className="px-3 py-1 rounded bg-bardin-ink text-white text-xs font-semibold disabled:opacity-50">Add field</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)} className="text-sm text-bardin-orange font-semibold hover:opacity-80">➕ New custom field</button>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          {confirmReset ? (
            <>
              <span className="text-xs text-slate-600 mr-auto">Reset to default for everyone?</span>
              <button onClick={() => setConfirmReset(false)} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
              <button onClick={onReset} disabled={saving} className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">Reset</button>
            </>
          ) : (
            <>
              <button onClick={() => setConfirmReset(true)} className="text-xs text-slate-500 hover:text-slate-700 mr-auto">Reset to default</button>
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {saving ? "Saving…" : "Save layout"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
